/**
 * Shared serve-mode helpers for the DCS content + SEO Vite plugins.
 *
 * In a production build the plugins bake the `.dcs/*.yaml` payload into a Vite
 * `define` literal (`define: { __DCS_X__: JSON.stringify(data) }`) so the SSG /
 * SPA bundle inlines it exactly as before.
 *
 * In dev (`vite` / `vitepress dev`, i.e. the portal preview) baking a literal
 * is wrong: the value is frozen at server-start and only a `server.restart()`
 * would refresh it — and that restart is exactly what triggers VitePress's
 * MiniSearch rebuild and the duplicate-heading "server restart failed" crash.
 *
 * Instead, in serve mode the plugins:
 *   1. redirect the `__DCS_X__` define to a browser global
 *      (`define: { __DCS_X__: 'globalThis.__DCS_X__' }`), and
 *   2. inject an inline classic `<script>` at the top of `<head>` on every HTML
 *      request that assigns the FRESH on-disk YAML to that global.
 *
 * Because `transformIndexHtml` re-runs on every dev HTML request, a plain
 * iframe reload (the portal's `?_hc` hard reload) reflects the latest on-disk
 * content with NO `server.restart()` and NO module-graph surgery. The consumer
 * is untouched: it still reads the bare `__DCS_X__` identifier, which the define
 * rewrites to `globalThis.__DCS_X__`.
 */

import fs from 'node:fs'
import path from 'node:path'
import yaml from 'js-yaml'

/**
 * The three `.dcs` search locations the plugins have always used. VitePress
 * projects have the `docs/` folder as root, so the parent is checked too.
 */
export function dcsSearchPaths(projectRoot: string, relPath: string): string[] {
  return [
    path.resolve(projectRoot, relPath),
    path.resolve(projectRoot, '..', relPath),
    path.resolve(process.cwd(), relPath),
  ]
}

/** First existing `.dcs` file from the standard search, or `undefined`. */
export function resolveDcsFile(projectRoot: string, relPath: string): string | undefined {
  return dcsSearchPaths(projectRoot, relPath).find((p) => fs.existsSync(p))
}

/**
 * Read + parse a `.dcs` YAML file FRESH from disk on every call (no caching).
 * Returns the parsed value, or `undefined` when the file is missing or
 * unparseable. Never throws — a dev HTML request must never 500 on bad YAML.
 */
export function readDcsYamlFresh<T = unknown>(
  projectRoot: string,
  relPath: string,
  label: string,
  debug = false
): T | undefined {
  const found = resolveDcsFile(projectRoot, relPath)
  if (!found) {
    if (debug) console.log(`[${label}] serve: no ${relPath} found; global left unset`)
    return undefined
  }
  try {
    return yaml.load(fs.readFileSync(found, 'utf8')) as T
  } catch (error) {
    console.warn(`[${label}] serve: failed to parse ${relPath}:`, error)
    return undefined
  }
}

/**
 * Build a safe inline `<script>` body that assigns a JSON-serialisable value to
 * a browser global. Every `<` is escaped to its `<` unicode form so an
 * embedded `</script>` (or `<!--`) inside the content data can never break out
 * of the inline script.
 */
export function buildGlobalAssignScript(globalName: string, value: unknown): string {
  const json = JSON.stringify(value ?? null).replace(/</g, '\\u003c')
  return `globalThis.${globalName} = ${json};`
}
